import { autoinject, bindingMode, computedFrom, customElement } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ValidationRules } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { cloneDeep } from 'framework/script_utilities/object-equalizer';
import { CountryChangeRequestData, CountryChangeRequestModel } from 'model/forms/country-change-request-data';
import { SystemUserWrapper } from 'model/support-types';
import { EnumsService } from 'services/enums-service';
import { KeywordsService } from 'services/keywords-service';
import { LookupsService } from 'services/lookups-service';
import { MAX_DATE } from '../support/settings';

@autoinject
@customElement('country-change-request-form')
export class CountryChangeRequestFormCustomElement {
  @bindable({ defaultBindingMode: bindingMode.fromView }) changed = false;
  @bindable model: CountryChangeRequestModel = null;
  dataBackup: CountryChangeRequestData = null;
  @bindable autofocus = false;
  newEmptySystemUserWrapper: SystemUserWrapper = { user: null, userId: null };
  @bindable({ defaultBindingMode: bindingMode.fromView }) approversEditing = false;
  public today = new Date().toISOString().slice(0, 10);
  public maxDate = MAX_DATE.toISOString().slice(0, 10);

  constructor(private keywords: KeywordsService, private enumService: EnumsService, private lookups: LookupsService) {
  }

  modelChanged() {
    this.dataBackup = cloneDeep(this.model.data);
    this.newEmptySystemUserWrapper = { user: null } as SystemUserWrapper;
    ValidationRules
      .ensureObject<CountryChangeRequestModel>().required()
      .ensure(model => model.requestedBy).displayName('Requested By').required()
      .ensure(model => model.effectiveFrom).displayName('Effective From').required().satisfiesRule('minDate').satisfiesRule('maxDate')
      .on(this.model);
    ValidationRules
      .ensureObject<CountryChangeRequestData>().required()
      .ensure(model => model.approvalLevel).displayName('Approval Level').required()
      .ensure(model => model.approvalRequirement).displayName('Approval').required()
      .ensure(model => model.approveType).displayName('Approval Category').required()
      .on(this.model.data);
  }

  keywordsItemMatcher = this.keywords.itemsMatch;
  enumsItemMatcher = this.enumService.itemsMatch;

  @computedFrom('keywords.approvalLevels', 'model.data.approvalLevel')
  get approvalLevels() {
    return this.keywords.ensureItem(this.keywords.approvalLevels, this.model.data.approvalLevel);
  }

  @computedFrom('model.requestedBy')
  get userGroupLabel() {
    const labels = [this.model.requestedBy?.jobTitle, this.model.requestedBy?.mail, this.model.requestedBy?.phone].filter(l => l != null);
    return labels.length > 0 ? labels : 'Requested by';
  }

  usersLookupOptions = this.lookups.getUsersLookupOptions();

  addNewApprover(item: SystemUserWrapper) {
    const existingApprover = this.model.data.approversWrapper.find(x => x.user.uniqueid === item.user.uniqueid);
    if (existingApprover == null) {
      this.model.data.approversWrapper.unshift(item);
      this.model.data.approversWrapper = this.model.data.approversWrapper.sort((a, b) => ('' + a.user.name).localeCompare(b.user.name));
    }
  }

  @watch((x: CountryChangeRequestFormCustomElement) => x.model.data.approvalLevel)
  @watch((x: CountryChangeRequestFormCustomElement) => x.model.data.approvalRequirement)
  @watch((x: CountryChangeRequestFormCustomElement) => x.model.data.approveType)
  @watch((x: CountryChangeRequestFormCustomElement) => x.model.data.approversWrapper.length)
  dataChanged() {
    this.model.changes = [];
    if (this.model.data.approvalRequirement == null || this.model.data.approvalRequirement.value !== this.dataBackup.approvalRequirement.value) {
      this.model.changes.push(`Approval: ${this.dataBackup.approvalRequirement.name} -> ${this.model.data.approvalRequirement != null ? this.model.data.approvalRequirement.name : ''}`);
    }
    if (this.model.data.approvalLevel == null || this.model.data.approvalLevel.id !== this.dataBackup.approvalLevel.id) {
      this.model.changes.push(`Approval Level: ${this.dataBackup.approvalLevel.name} -> ${this.model.data.approvalLevel != null ? this.model.data.approvalLevel.name : ''}`);
    }
    if (this.model.data.approveType == null || this.model.data.approveType.value !== this.dataBackup.approveType.value) {
      this.model.changes.push(`Approval Category: ${this.dataBackup.approveType.name} -> ${this.model.data.approveType != null ? this.model.data.approveType.name : ''}`);
    }
    const approversToRemove = this.dataBackup.approversWrapper.filter(x => !this.model.data.approversWrapper.some(a => a.user.uniqueid === x.user.uniqueid));
    const approversToAdd = this.model.data.approversWrapper.filter(x => !this.dataBackup.approversWrapper.some(a => a.user.uniqueid === x.user.uniqueid));
    if (approversToRemove.length > 0) {
      this.model.changes.push(`Approver(s) to remove: ${approversToRemove.map(x => x.user.name).sort((a, b) => ('' + a).localeCompare(b)).join(', ')}`);
    }
    if (approversToAdd.length > 0) {
      this.model.changes.push(`Approver(s) to add: ${approversToAdd.map(x => x.user.name).sort((a, b) => ('' + a).localeCompare(b)).join(', ')}`);
    }
    this.changed = this.model.changes.length > 0;
  }
}
